import React from 'react';
import {
  SPINNER_SIZE_MD,
  SPINNER_SIZE_TOKENS,
  type SpinnerSize,
} from './Spinner.constants';
import { Root } from './Spinner.styled';

export interface SpinnerProps {
  /**
   * Preset size token. Picks a diameter + stroke width that aligns with the
   * icon slot of {@link IconButton} at the same size — `sm` (14px) for inline
   * text, `md` (18px) for buttons / status pills, `lg` (24px) for standalone
   * loading blocks. For pixel-perfect control pass {@link diameter} instead.
   */
  size?: SpinnerSize;
  /**
   * Override the preset diameter (in pixels). Stroke width scales linearly
   * to keep the visual weight consistent. Use this when the spinner needs
   * to match a non-standard glyph size (e.g. a custom 32px chevron).
   */
  diameter?: number;
  /**
   * Accessible name announced by screen readers. Defaults to "Loading"; use
   * something more specific when the spinner replaces a labelled control
   * (e.g. "Publishing" while a publish action is in flight).
   */
  label?: string;
  className?: string;
  dataTestId?: string;
}

/**
 * Indeterminate progress indicator — a single spinning quarter-arc rendered
 * via a CSS keyframe rotation on a circular border with one transparent side.
 * Inherits its colour from `currentColor`, so the parent controls hue by
 * setting its own colour (e.g. inside a primary-tinted button it picks up
 * the button's foreground).
 *
 * Sized to align with `IconButton`'s icon slot at the same token — `sm`/`md`/
 * `lg` map to 14/18/24px diameters. For one-off sizes pass {@link diameter}.
 *
 * @example
 *   <Spinner size="sm" />                    // inline-with-text
 *   <Button leadingIcon={<Spinner size="sm" />} loading>Publishing</Button>
 *   <Spinner diameter={32} label="Saving" /> // custom diameter
 */
const Spinner: React.FC<SpinnerProps> = ({
  size = SPINNER_SIZE_MD,
  diameter,
  label = 'Loading',
  className,
  dataTestId = 'spinner',
}) => {
  const tokens = SPINNER_SIZE_TOKENS[size];
  // Treat `diameter` as valid only when it's a positive finite number —
  // 0, negatives, NaN, and Infinity would all produce broken CSS values
  // (a 0×0 spinner, a NaN borderWidth, etc). Fall back to the size token.
  const diameterIsValid =
    typeof diameter === 'number' && Number.isFinite(diameter) && diameter > 0;
  const resolvedDiameter = diameterIsValid ? diameter : tokens.diameter;
  // Stroke width scales linearly with diameter so a spinner forced to 48px
  // doesn't end up with a hairline ring against its size.
  const resolvedStroke = diameterIsValid
    ? Math.max(1, Math.round((diameter / tokens.diameter) * tokens.stroke * 10) / 10)
    : tokens.stroke;

  return (
    <Root
      style={{
        width: `${resolvedDiameter}px`,
        height: `${resolvedDiameter}px`,
        borderWidth: `${resolvedStroke}px`,
      }}
      role="status"
      aria-label={label}
      aria-live="polite"
      className={className}
      data-testid={dataTestId}
    />
  );
};

export default Spinner;
